import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useEffect } from "react";
import { useForm } from "@mantine/form";
import {
  DateValue,
  getLocalTimeZone,
  parseDate,
} from "@internationalized/date";
import dayjs from "dayjs";
import { api } from "@/lib/api-client";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import {
  GetCustomerSubscription,
  SubscriptionCreditType,
} from "@/server-types";
import { getLocalizedName } from "@/utils";

type CreditFormValues = {
  type: SubscriptionCreditType;
  amount: number;
  validTo: DateValue;
  serviceId: string;
  reason: string;
};

interface CustomerSubscriptionCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerSubscription: GetCustomerSubscription["response"]["customerSubscription"];
}

export const CustomerSubscriptionCreditModal = ({
  isOpen,
  onClose,
  customerSubscription,
}: CustomerSubscriptionCreditModalProps) => {
  const today = dayjs().startOf("day");
  const subscriptionValidTo = dayjs(customerSubscription.validTo).startOf(
    "day",
  );
  const defaultValidTo = subscriptionValidTo.isBefore(today)
    ? today
    : subscriptionValidTo;
  const {
    mutateAsync: postCredit,
    isPending: isPostingCredit,
    processedErrorMessage: postCreditError,
  } = api.customer.usePostCustomerSubscriptionCredit();

  const form = useForm<CreditFormValues>({
    initialValues: {
      type: "manual",
      amount: 1,
      validTo: parseDate(defaultValidTo.format("YYYY-MM-DD")),
      serviceId: "all",
      reason: "",
    },
    validate: {
      amount: (value) => (value > 0 ? undefined : t`Vnesite veljavno količino`),
      validTo: (value) => {
        const selectedDate = dayjs(value.toDate(getLocalTimeZone())).startOf(
          "day",
        );
        if (selectedDate.isBefore(today)) {
          return t`Datum mora biti v prihodnosti`;
        }
        return undefined;
      },
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset();
  }, [isOpen]);

  const creditTypeOptions = [
    { key: "manual", label: t`Ročno dodani` },
    { key: "makeup", label: t`Nadomestni termin` },
  ];

  const serviceOptions = [
    { key: "all", label: t`Vse storitve v paketu` },
    ...customerSubscription.customerSubscriptionServices.map((service) => ({
      key: service.serviceId.toString(),
      label: getLocalizedName(service.service.nameLocalized),
    })),
  ];

  const handleSubmit = async (values: CreditFormValues) => {
    try {
      const serviceId =
        values.serviceId === "all" ? undefined : Number(values.serviceId);

      await postCredit({
        customerSubscriptionId: customerSubscription.id,
        body: {
          type: values.type,
          amount: values.amount,
          validTo: values.validTo.toDate(getLocalTimeZone()),
          serviceId,
          reason: values.reason || undefined,
        },
      });

      addToast({
        title: t`Dodatni obiski dodani`,
        description: t`Kredit je bil uspešno dodan.`,
        color: "success",
      });

      onClose();
    } catch {
      addToast({
        title: t`Napaka`,
        description: postCreditError,
        color: "danger",
      });
    }
  };

  return (
    <LimeModal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <Trans>Dodaj dodatne obiske</Trans>
        </ModalHeader>
        <form onSubmit={form.onSubmit(handleSubmit)} autoComplete="off">
          <ModalBody>
            <div className="flex flex-col gap-4">
              <LimeSelect
                label={t`Tip kredita`}
                items={creditTypeOptions}
                {...form.getInputProps("type")}
              />
              <LimeNumberInput
                label={t`Količina obiskov`}
                minValue={1}
                step={1}
                {...form.getInputProps("amount")}
              />
              <LimeDatePicker
                label={t`Obiske mogoče porabiti do`}
                minValue={parseDate(today.format("YYYY-MM-DD"))}
                value={form.values.validTo}
                {...form.getInputProps("validTo")}
                onChange={(value) => {
                  if (!value) return;
                  form.setFieldValue("validTo", value);
                }}
              />
              <LimeSelect
                label={t`Storitev (neobvezno)`}
                items={serviceOptions}
                {...form.getInputProps("serviceId")}
              />
              <LimeInput
                label={t`Razlog (neobvezno)`}
                placeholder={t`Vnesite razlog za dodani kredit`}
                {...form.getInputProps("reason")}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={onClose}
              isDisabled={isPostingCredit}
            >
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              color="primary"
              variant="solid"
              type="submit"
              isLoading={isPostingCredit}
            >
              <Trans>Dodaj</Trans>
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </LimeModal>
  );
};
